import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "antd";

import { SORTS } from "../detailService";
import { MIN_RATING_THRESHOLD, countActiveProductFilters } from "../productSuppliers";

export const SORT_OPTIONS = [
  { id: "distance", label: "Distance", sort: SORTS.DISTANCE, minRating: null },
  { id: "rating-any", label: "Top rated", sort: SORTS.RATING, minRating: null },
  {
    id: "rating-4",
    label: "Rated 4.0+",
    sort: SORTS.RATING,
    minRating: MIN_RATING_THRESHOLD,
  },
  { id: "price-asc", label: "Lowest price", sort: SORTS.PRICE_ASC, minRating: null },
  { id: "price-desc", label: "Highest price", sort: SORTS.PRICE_DESC, minRating: null },
  { id: "discounted", label: "Discounted", sort: SORTS.DISCOUNTED, minRating: null },
];

export const sortOptionId = (filters) =>
  SORT_OPTIONS.find(
    (option) =>
      option.sort === filters.sort &&
      (option.minRating ?? null) === (filters.minRating ?? null)
  )?.id || "distance";

export const sortLabel = (filters) =>
  SORT_OPTIONS.find((option) => option.id === sortOptionId(filters))?.label ||
  "Distance";

const Toggle = ({ label, checked, onChange }) => (
  <label className="sy-filter-toggle">
    <span>{label}</span>

    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />

    <i aria-hidden="true" />
  </label>
);

const ProductFiltersPanel = ({
  open,
  filters,
  bounds,
  asSheet,
  onClose,
  onApply,
  onReset,
}) => {
  const ceiling = Math.max(bounds?.maxDistanceKm || 1, 1);

  const [draft, setDraft] = useState(filters);
  const [distance, setDistance] = useState(filters.maxDistanceKm ?? ceiling);
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setDraft(filters);
    setDistance(filters.maxDistanceKm ?? ceiling);
  }, [open, filters, ceiling]);

  useEffect(() => {
    if (!open) return;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const activeCount = useMemo(
    () => countActiveProductFilters({ ...draft, maxDistanceKm: distance }, bounds),
    [draft, distance, bounds]
  );

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const tapApply = () => {
    pendingRef.current = {
      type: "apply",
      values: { ...draft, maxDistanceKm: distance },
    };

    onClose();
  };

  const tapReset = () => {
    pendingRef.current = { type: "reset" };
    onClose();
  };

  const onAfterClose = (isOpen) => {
    if (isOpen) return;

    const pending = pendingRef.current;
    pendingRef.current = null;

    if (!pending) return;
    if (pending.type === "reset") onReset();
    else onApply(pending.values);
  };

  const selectedSort = sortOptionId(draft);
  const percent = ceiling <= 1 ? 1 : (distance - 1) / (ceiling - 1);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={onAfterClose}
      placement={asSheet ? "bottom" : "right"}
      height={asSheet ? "82%" : undefined}
      width={asSheet ? undefined : "min(440px, 100vw)"}
      title={
        <header className="home-panel-header-card">
          <div className="home-panel-heading">
            <h2 className="home-panel-title">Filters</h2>
            <p className="home-panel-subtitle">
              {activeCount > 0
                ? `${activeCount} ${activeCount === 1 ? "filter" : "filters"} applied`
                : "Narrow down what you see"}
            </p>
          </div>

          <button
            type="button"
            className="home-panel-close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <img
              src="/assets/icons/panel-close.svg"
              width="24"
              height="24"
              alt=""
              aria-hidden="true"
            />
          </button>
        </header>
      }
      closable={false}
      rootClassName="home-filters-drawer"
      footer={
        <div className="home-filter-footer">
          <button
            type="button"
            className="home-filter-reset"
            disabled={countActiveProductFilters(filters, bounds) === 0}
            onClick={tapReset}
          >
            Clear All
          </button>

          <button type="button" className="home-filter-apply" onClick={tapApply}>
            Apply{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </div>
      }
    >
      <fieldset className="home-filter-group">
        <legend>Sort by</legend>

        <div className="home-filter-chips" role="radiogroup" aria-label="Sort by">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selectedSort === option.id}
              className={`home-filter-chip${
                selectedSort === option.id ? " is-selected" : ""
              }`}
              onClick={() =>
                update({ sort: option.sort, minRating: option.minRating })
              }
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="home-filter-group">
        <div className="home-filter-head">
          <label htmlFor="sy-filter-distance">Maximum distance</label>
          <output htmlFor="sy-filter-distance">{distance}km</output>
        </div>

        <div className="home-slider" style={{ "--pct": percent }}>
          <span className="home-slider-bubble" aria-hidden="true">
            {distance}
            <small>km</small>
          </span>

          <div className="home-slider-rail">
            <div className="home-slider-track" aria-hidden="true">
              <span className="home-slider-fill" />
            </div>

            <input
              id="sy-filter-distance"
              className="home-slider-input"
              type="range"
              min={1}
              max={ceiling}
              step={1}
              value={distance}
              aria-valuetext={`${distance} kilometres`}
              onChange={(event) => setDistance(Number(event.target.value))}
            />
          </div>

          <div className="home-slider-scale" aria-hidden="true">
            <span>1km</span>
            <span>{ceiling}km</span>
          </div>
        </div>
      </div>

      <div className="home-filter-group">
        <Toggle
          label="Discounted only"
          checked={draft.discountedOnly}
          onChange={(discountedOnly) => update({ discountedOnly })}
        />

        <Toggle
          label="Include out of stock"
          checked={draft.includeOutOfStock}
          onChange={(includeOutOfStock) => update({ includeOutOfStock })}
        />
      </div>

      {draft.minRating === MIN_RATING_THRESHOLD && (
        <p className="home-filter-hint">
          The rating threshold is applied to the suppliers already fetched, so the
          count can be small even when more qualify further out.
        </p>
      )}
    </Drawer>
  );
};

export default React.memo(ProductFiltersPanel);
